import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import moment from "moment";
import {
  getFormMembersDetails_V1,
  getTemplateDetail_V1,
} from "src/DAL/Forms/forms";
import { gr } from "src/design-system/tokens";
import GrButton from "src/design-system/components/GrButton";
import GrCard from "src/design-system/components/GrCard";
import GrCardHeader from "src/design-system/components/GrCardHeader";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrPage from "src/design-system/components/GrPage";
import GrStatusPill from "src/design-system/components/GrStatusPill";
import GrBadge from "src/design-system/components/GrBadge";
import GrAnswerRow from "src/design-system/components/GrAnswerRow";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrLoader from "src/design-system/components/GrLoader";
import GrText from "src/design-system/components/GrText";
import AvatarGalleryPreview from "./component/imageViewOnLengthPreview";

/**
 * A form's answers (/forms-Details/:id/:user): "View answers" for a submitted form,
 * "Preview" for a pending one. Coach feedback sits beside the answers.
 * Same APIs as before (get_answers_or_questions + list_feedback).
 */

const backLinkSx = {
  color: gr.color.ink2,
  fontWeight: gr.font.weight.regular,
  "&:hover": { color: gr.color.brandDark },
};
// With coach feedback: answers + feedback side by side. Without: one 760px column in the middle
// (header included, like the Fill in page).
const pageSx = (withSide) => (withSide ? {} : { maxWidth: 760, mx: "auto" });
const layoutSx = (withSide) => ({
  display: "grid",
  gridTemplateColumns: withSide
    ? { xs: "1fr", lg: "minmax(0, 1fr) minmax(300px, 380px)" }
    : "1fr",
  gap: gr.space[6],
  alignItems: "start",
});
const pillSx = { alignSelf: "center" };
const badgesSx = { display: "flex", flexWrap: "wrap", gap: gr.space[2] };
const signatureSx = {
  display: "block",
  maxWidth: "100%",
  width: `calc(${gr.space[12]} * 7)`,
  border: `1px solid ${gr.color.line}`,
  borderRadius: gr.radius.tile,
  background: gr.color.surface,
};
const headerActionsSx = {
  display: "flex",
  alignItems: "center",
  gap: gr.space[3],
};
const feedbackSx = {
  py: gr.space[4],
  "&:first-of-type": { pt: 0 },
  "&:last-of-type": { pb: 0 },
  "& + &": { borderTop: `1px solid ${gr.color.line2}` },
};
const feedbackHtmlSx = {
  mt: gr.space[2],
  fontFamily: gr.font.family,
  fontSize: gr.font.size.md,
  color: gr.color.ink,
  lineHeight: gr.font.lineHeight.normal,
  overflowWrap: "anywhere",
  "& p": { m: 0, mb: gr.space[2] },
  "& p:last-child": { mb: 0 },
  "& img": { maxWidth: "100%" },
};

const hasAnswer = (q) =>
  q.type === "multiple_select" || q.type === "image"
    ? (q.multiple_answers || []).some(Boolean)
    : String(q.answer || "").trim() !== "";

/** The answer of one question, shown the way it was given. */
const answerOf = (q) => {
  if (!hasAnswer(q)) return null;
  if (q.type === "multiple_choice")
    return <GrBadge tone="brand">{q.answer}</GrBadge>;
  if (q.type === "multiple_select") {
    return (
      <Box sx={badgesSx}>
        {q.multiple_answers.filter(Boolean).map((option) => (
          <GrBadge key={option} tone="brand">
            {option}
          </GrBadge>
        ))}
      </Box>
    );
  }
  if (q.type === "image") {
    return (
      <AvatarGalleryPreview
        length={q.options_count}
        Image={q.multiple_answers || []}
      />
    );
  }
  if (q.type === "signature") {
    return (
      <Box component="img" src={q.answer} alt="Signature" sx={signatureSx} />
    );
  }
  return q.answer;
};

export default function DetailsFormInformation() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { slug: formId, user_id: userId } = useParams();
  const [form, setForm] = useState(state || {});
  const [questions, setQuestions] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loadState, setLoadState] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadState("loading");
      const [answers, details] = await Promise.all([
        getFormMembersDetails_V1(formId, userId),
        getTemplateDetail_V1(formId, userId, "individual"),
      ]);
      if (!alive) return;
      if (details?.code !== 200) {
        enqueueSnackbar(details?.message || "Could not open this form", {
          variant: "error",
        });
        navigate("/forms");
        return;
      }
      setForm((current) => ({ ...(details.form || {}), ...(current || {}) }));
      setFeedback(
        [...(details.data || [])].sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        )
      );
      if (answers?.code === 200) {
        setQuestions(answers?.data?.questions || []);
        setLoadState("ready");
      } else {
        enqueueSnackbar(answers?.message || "Could not load the answers", {
          variant: "error",
        });
        setLoadState("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [formId, userId, enqueueSnackbar, navigate]);

  const submitted =
    form?.submission_status === "submitted" ||
    (form?.submission_status == null && questions.some(hasAnswer));
  const submittedOn = form?.answers?.[0]?.createdAt;
  const goBack = () => navigate("/forms");
  const fillIn = () =>
    navigate(`/forms-submition/${form.form_slug}`, { state: form });

  return (
    <GrPage>
      <Box sx={pageSx(feedback.length > 0)}>
        <Box sx={{ mb: gr.space[6] }}>
          <GrButton variant="link" onClick={goBack} sx={backLinkSx}>
            ← Your Information
          </GrButton>
        </Box>

        {loadState === "loading" ? (
          <GrLoader label="Opening the form" />
        ) : (
          <>
            <GrPageHeader
              title={form?.title || "Form"}
              subtitle={form?.description || null}
              actions={
                <Box sx={headerActionsSx}>
                  <GrStatusPill status={submitted ? "ok" : "due"} sx={pillSx}>
                    {submitted
                      ? `Submitted${
                          submittedOn
                            ? ` ${moment(submittedOn).format("D MMM YYYY")}`
                            : ""
                        }`
                      : "Pending"}
                  </GrStatusPill>
                  {!submitted && form?.form_slug ? (
                    <GrButton onClick={fillIn}>Fill in</GrButton>
                  ) : null}
                </Box>
              }
            />

            <Box sx={layoutSx(feedback.length > 0)}>
              <GrCard>
                <GrCardHeader
                  as="h2"
                  title={submitted ? "Your answers" : "Questions"}
                  subtitle={
                    submitted
                      ? `${questions.filter(hasAnswer).length} of ${
                          questions.length
                        } answered`
                      : "You haven't filled in this form yet."
                  }
                />
                {loadState === "error" ? (
                  <GrEmptyState
                    compact
                    title="The answers could not be loaded."
                  />
                ) : questions.length === 0 ? (
                  <GrEmptyState compact title="This form has no questions." />
                ) : (
                  <Box>
                    {questions.map((q, index) => (
                      <GrAnswerRow
                        key={q._id || index}
                        number={index + 1}
                        question={q.title || `Question ${index + 1}`}
                        answer={answerOf(q)}
                        emptyText={
                          submitted ? "Not answered" : "Not answered yet"
                        }
                      />
                    ))}
                  </Box>
                )}
              </GrCard>

              {feedback.length > 0 ? (
                <GrCard>
                  <GrCardHeader
                    as="h2"
                    title="Coach feedback"
                    subtitle={`${feedback.length} note${
                      feedback.length === 1 ? "" : "s"
                    } from your coach`}
                  />
                  <Box>
                    {feedback.map((item, index) => (
                      <Box key={item._id || index} sx={feedbackSx}>
                        <GrText variant="caption">
                          {item.createdAt
                            ? moment(item.createdAt).format(
                                "D MMM YYYY [at] HH:mm"
                              )
                            : "Feedback"}
                        </GrText>
                        <Box
                          sx={feedbackHtmlSx}
                          dangerouslySetInnerHTML={{
                            __html: item.feedback_content || "",
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </GrCard>
              ) : null}
            </Box>
          </>
        )}
      </Box>
    </GrPage>
  );
}
